import { asyncCallWithTimeout, TIMEOUT_MESSAGE } from './asyncTimeout';
import getUserDetails from './getUserDetails';
import { getStorageObject, saveStorageObject } from './localStorage';
import { getCookie, saveCookie } from './cookieStorage';

/**
 * @function getVariationsFromDE
 * @param {{
 * campaignIds: [{campaignId}],
 * additionalDetails: {},
 * channel: {channel: String},
 * apiEndpoint: String
 * storageOpts: {expiration: Number, isCookieStorage: Boolean, isLocalStorage: Boolean}
 * }} config
 * @returns {Promise<[{response_details: [{campaign_name: String, decision: String}]}]>}
 */
export const getVariationsFromDE = async ({
  campaignIds,
  additionalDetails,
  apiEndpoint,
  channel,
  storageOpts = {}
}) => {
  window.LIFE_CYCLE_EVENT_BUS.trigger('decision-engine.api-call', { loading: true });
  const timestamp = new Date(Math.round(Date.now() / 1000)).getTime();

  const uniqueCampaignIds = campaignIds.filter((campaign, index) => {
    return campaignIds.findIndex((camp) => camp.campaignId === campaign.campaignId) === index;
  });

  const campaignDTLS = uniqueCampaignIds.map(({ campaignId: campaignName }) => ({
    campaign_name: campaignName,
    additional_dtls: {
      // channel: channel || '',
      ...additionalDetails,
    },
  }));

  const firstCampaignName = campaignDTLS?.[0]?.campaign_name;
  let storageKey = storageOpts.key;
  if (!storageKey) {
    storageKey = storageOpts.isLocalStorage
      ? `decisionEngine.${firstCampaignName}`
      : firstCampaignName;
  }

  let deResponseFromStorage = null;

  if (storageOpts.isLocalStorage) {
    deResponseFromStorage = getStorageObject({
      key: storageKey
    })?.response;

    // reset timestamp section in post_de for analytics
    if (deResponseFromStorage?.response_details?.[0]?.post_de) {
      const postDEArr = deResponseFromStorage.response_details[0].post_de.split('|');
      postDEArr[3] = timestamp;
      deResponseFromStorage.response_details[0].post_de = postDEArr.join('|');
    }
  } else if (storageOpts.isCookieStorage) { // EdgeWorker cookie - to be converted into usable response format
    const edgeWorkerCookieValue = getCookie({
      key: 'Experiment'
    });
    // If the cookie value is not empty, transform it into a response format
    if (edgeWorkerCookieValue?.decision_engine?.[storageKey]) {
      const {
        arm = '',
        name = '',
        test_group: testGroup = ''
      } = edgeWorkerCookieValue.decision_engine[storageKey] || {};
      deResponseFromStorage = {
        response_details: [
          {
            campaign_name: firstCampaignName,
            decision: arm,
            post_de: `${name}|${testGroup}|${arm}|${timestamp}`,
            test_group_id: `${name}_${testGroup}`
          },
        ]
      };
    }
  }

  const init = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      user_dtls: getUserDetails(),
      tracking_dtls: {
        timestamp,
        source_system: 'personalization-util',
      },
      campaign_dtls: campaignDTLS,
      debug: window.isDebugMode,
    }),
  };

  try {
    let response = {};
    if (!deResponseFromStorage) {
      const req = await asyncCallWithTimeout(
        fetch(`${apiEndpoint}/decision-engine-conductor/v1/experience`, init),
        5000
      );
      response = await req.json();
      if (response?.errorResponse?.errorMessage) {
        throw new Error(response.errorResponse.errorMessage);
      }

      if (storageOpts.isLocalStorage) {
        saveStorageObject({
          key: storageKey,
          response,
          expiration: storageOpts.expiration
        });
      } else if (storageOpts.isCookieStorage) {
        let edgeWorkerCookieValue = getCookie({ key: 'Experiment' }) || { decision_engine: {} };
        const {
          decision: arm, campaign_name: name, test_group_name: testGroup
        } = response?.response_details?.[0] || {};
        edgeWorkerCookieValue = {
          ...edgeWorkerCookieValue,
          decision_engine: {
            ...edgeWorkerCookieValue.decision_engine || {},
            [storageKey]: {
              arm,
              name,
              test_group: testGroup
              // test_group_id is not needed in the cookie
            }
          }
        };
        saveCookie({
          key: 'Experiment',
          response: edgeWorkerCookieValue,
          expiration: storageOpts.expiration,
        });
      }
    } else {
      response = deResponseFromStorage;
    }

    // eslint-disable-next-line camelcase
    const { post_de } = response?.response_details?.[0] || {};

    window.LIFE_CYCLE_EVENT_BUS.trigger('decision-engine.api-call', {
      post_de,
      loading: false
    });
    return [response];
  } catch (error) {
    window.LIFE_CYCLE_EVENT_BUS.trigger('decision-engine.api-call', { loading: false, error });
    if (error.message === TIMEOUT_MESSAGE) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('personalization-utils.timeout', {
        source: 'Decision Engine',
        error: error.message,
        payload: { body: init.body, ids: uniqueCampaignIds },
      });
    } else {
      window.LIFE_CYCLE_EVENT_BUS.trigger('personalization-utils.deError', {
        error: { status: error?.errorResponse?.status, message: error?.errorMessage },
      });
    }
    /* eslint-disable-next-line no-console, no-unused-expressions */
    window.isDebugMode && console.error(error);
    return [];
  }
};
